import React, { memo, useMemo } from 'react';
import { ActionButton, Tooltip, TooltipTrigger } from '@adobe/react-spectrum';
import { CardTitleDisplay } from 'app/components/TimelineCard/CardTitleDisplay';
import { isEolMoment } from 'utils/constants';
import { formatEolPhaseDisplayTitle } from 'utils/eolPhaseTitlePrefix';
import { getRtmPhasedTitleParts } from 'utils/rtmPhaseDisplayTitle';
import {
  DelayedTooltipState,
  useDelayedTooltip
} from '../hooks/useDelayedTooltip';
import { isKeyDateLane } from '../utils/monthlyItemIconUtils';
import type { MonthlyTimelineItem } from '../utils/types';
import styles from '../MonthlyRoadmap.module.scss';

interface MonthlyItemTitleProps {
  title: string;
  laneLabel: string;
  projectType?: string;
  releasePhase?: string;
  releasePhaseId?: string;
  releasePhases?: MonthlyTimelineItem['releasePhases'];
  isChild?: boolean;
  issueId?: string;
  onPress?: () => void;
  tooltip?: DelayedTooltipState;
}

export const MonthlyItemTitle = memo(
  ({
    title,
    laneLabel,
    projectType,
    releasePhase,
    releasePhaseId,
    releasePhases,
    isChild = false,
    issueId,
    onPress,
    tooltip: sharedTooltip
  }: MonthlyItemTitleProps) => {
    const localTooltip = useDelayedTooltip();
    const {
      openTooltip,
      setOpenTooltip,
      handleTooltipMouseEnter,
      handleTooltipMouseLeave
    } = sharedTooltip ?? localTooltip;

    const isEventLane = laneLabel.toLowerCase().includes('event');
    const isKeyDate = isKeyDateLane(laneLabel);
    const titleClassName = `${styles.monthlyItemTitle} ${
      isKeyDate
        ? styles.monthlyItemTitleKeyDate
        : isChild || isEventLane
        ? styles.monthlyItemTitleMedium
        : styles.monthlyItemTitleBold
    }`;
    const titleParts = useMemo(
      () =>
        getRtmPhasedTitleParts({
          momentType: projectType,
          rawTitle: title,
          releasePhase,
          releasePhaseId,
          releasePhases: Array.isArray(releasePhases) ? releasePhases : null
        }),
      [projectType, releasePhase, releasePhaseId, releasePhases, title]
    );
    const isEolCard = isEolMoment(projectType);
    const tooltipTitle = isEolCard
      ? formatEolPhaseDisplayTitle(releasePhase, title)
      : titleParts.displayTitle;

    return (
      <TooltipTrigger
        isOpen={openTooltip === 'title'}
        onOpenChange={isOpen => {
          if (!isOpen) {
            setOpenTooltip(null);
          }
        }}
        placement="top"
      >
        <div
          className={styles.monthlyItemTitleTooltipWrapper}
          onMouseEnter={() => handleTooltipMouseEnter('title')}
          onMouseLeave={handleTooltipMouseLeave}
        >
          <ActionButton
            data-testid={
              issueId ? `monthly-item-title-${issueId}` : 'monthly-item-title'
            }
            isQuiet
            onPress={onPress}
            UNSAFE_className={styles.monthlyItemTitleButton}
            aria-label={tooltipTitle}
          >
            <CardTitleDisplay
              projectType={projectType}
              releasePhase={releasePhase}
              title={title}
              displayTitle={titleParts.displayTitle}
              className={titleClassName}
            />
          </ActionButton>
        </div>
        <Tooltip>{tooltipTitle}</Tooltip>
      </TooltipTrigger>
    );
  }
);
